import { useState } from "react";
import {
  Box,
  Button as MuiButton,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TableBody,
  Table as DataTable,
  TableSortLabel,
  TablePagination,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import clsx from "clsx";
import orderBy from "lodash/orderBy";

import styles from "./table.module.css";
import Loader from "../Loader/Loader";
import { colors } from "../../theme/colors";

interface Field {
  id: string;
  label: string;
  type?: string | undefined;
  flex?: number;
  sortable?: boolean;
  render?: (prop: any) => JSX.Element;
}

interface CustomTableProps<T> {
  fields: Field[];
  data: T[]; // local array
  isLargeRow?: boolean;
  onDoubleClick?: (item: any) => void;
  paginationRequired?: boolean;
  defaultOrder?: "Asc" | "Dsc";
  defaultOrderBy?: string;
  defaultRowsPerPage?: number;
  loading?: boolean;
  searchTerm?: string;
  onClearSearch?: () => void;
  // Per-row style hook, for emphasis a column cannot carry (e.g. a severity
  // border down the whole row). Receives the row item, returns MUI sx.
  rowSx?: (item: T) => Record<string, unknown>;
  // Optional data-testid per row, so callers can assert on a specific row.
  rowTestId?: (item: T) => string;
  // Floor for the table width. Cells still divide space by `flex`, but the
  // shares are computed against this instead of the container, so columns keep
  // a readable width and the container scrolls sideways when it is narrower.
  minTableWidth?: number | string;
}

const CustomTable = <T,>(props: CustomTableProps<T>) => {
  // Destructuring props to extract specific values
  const { data, fields, isLargeRow, onDoubleClick, paginationRequired = true, defaultOrder = "Asc", defaultOrderBy = "", defaultRowsPerPage = 10, loading = false, searchTerm, onClearSearch, minTableWidth, rowSx, rowTestId } = props;

  const [order, setOrder] = useState<"Asc" | "Dsc">(defaultOrder);
  const [orderby, setOrderby] = useState(defaultOrderBy);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);


  const createSortHandler = (property: any) => {
    const isDesc = orderby === property && order === "Dsc";
    setOrderby(property);
    setOrder(isDesc ? "Asc" : "Dsc");
    setPage(0); // Reset to first page on sort
  };

  // Sort data
  const orderedList = orderBy(
    data,
    [
      (item: any) => {
        const value = item[orderby];
        return typeof value === "string" ? value.toLowerCase() : value;
      },
    ],
    order === "Asc" ? "asc" : "desc"
  );

  // Pagination logic: slice sorted data for current page
  const paginatedList = paginationRequired
    ? orderedList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : orderedList;

  // Handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  // Render the table component
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: loading ? 400 : undefined,
        maxHeight: loading ? 400 : undefined,
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${colors.white}99`,
            zIndex: 2,
          }}
        >
          <Loader />
        </Box>
      )}
      <TableContainer
        sx={{
          background: "transparent",
          opacity: loading ? 0.5 : 1,
        }}
      >
        <DataTable sx={minTableWidth ? { minWidth: minTableWidth } : undefined}>
          <TableHead>
            <TableRow
              className={clsx(styles.rowBox, styles.tableHead, styles.tableRow)}
            >
              {fields.map(
                ({ sortable, label, id, flex = 1, type }: any, index: number) => {
                  return (
                    <TableCell
                      key={index}
                      className={styles.headerCell}
                      style={{ justifyContent: type === "badge" ? "center" : "left" }}
                      align="center"
                      width="100%"
                      sx={{ flex, minWidth: 0, borderBottomWidth: "0px !important" }}
                      sortDirection={orderby === id ? order === 'Asc' ? "asc" : "desc" : false}
                    >
                      {sortable ? (
                        <TableSortLabel
                          active={orderby === id}
                          direction={orderby === id ? order === 'Asc' ? "asc" : "desc" : "asc"}
                          onClick={() => {
                            createSortHandler(id);
                          }}
                          style={{ justifyContent: type === "badge" ? "center" : "left" }}
                        >
                          {label}
                        </TableSortLabel>
                      ) : (
                        <>{label}</>
                      )}
                    </TableCell>
                  );
                }
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={fields.length}
                  align="center"
                  sx={{ borderBottomWidth: "0px !important", py: 6 }}
                >
                  {searchTerm ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <SearchOutlinedIcon sx={{ fontSize: 64, color: "#bdbdbd" }} />
                      <Typography sx={{ fontSize: 16 }}>
                        <Box component="span" sx={{ fontWeight: 700 }}>No results found</Box>
                        {" "}matching <Box component="span" sx={{ fontWeight: 700 }}>&apos;{searchTerm}&apos;</Box>
                      </Typography>
                      {onClearSearch && (
                        <MuiButton variant="outlined" size="small" onClick={onClearSearch} sx={{ textTransform: "none" }}>
                          Clear search
                        </MuiButton>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                      No records found.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedList.map((item: any, index: number) => (
                <TableRow
                  className={clsx(
                    styles.rowBox,
                    isLargeRow && styles.largeRow,
                    styles.tableRow
                  )}
                  key={index}
                  hover
                  data-testid={rowTestId?.(item)}
                  sx={{ cursor: "pointer", ...rowSx?.(item) }}
                  onDoubleClick={() => onDoubleClick && onDoubleClick(item)}
                >
                  {fields.map(
                    ({ id, flex = 1, render = false, type = "text" }: any, index: any) => {
                      return (
                        <TableCell
                          key={index}
                          className={styles.cell}
                          style={{ justifyContent: type === "badge" ? "center" : "left" }}
                          align={type === "badge" ? "center" : "left"}
                          width="100%"
                          sx={{
                            flex,
                            display: "flex",
                            // Without this a cell's min-width defaults to auto,
                            // so wide content (long ids, status pills) widens the
                            // cell past its flex share and each row lands on its
                            // own column widths, out of step with the header.
                            minWidth: 0,
                            overflow: "hidden",
                            borderBottomWidth: "0px !important",
                          }}
                        >
                          {render ? (
                            render(item)
                          ) : (
                            <Typography
                              className={styles.cell}
                              sx={{
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: "2",
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item[id] ?? "-"}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    }
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </DataTable>
        {paginationRequired && data.length > defaultRowsPerPage && (
          <TablePagination<any>
            component="div"
            count={data.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            style={{
              display: "flex",
              justifyContent: "flex-start",
              marginLeft: "-20px",
            }}
          />
        )}
      </TableContainer>
    </Box>
  );
}

export default CustomTable;
